<template>
  <div class="import-wizard-overlay" v-if="isOpen">
    <div class="import-wizard-modal">
      <!-- Header -->
      <div class="wizard-header">
        <h2>📥 Kişi Listesi İçe Aktarma Sihirbazı</h2>
        <button class="close-btn" @click="close">✕</button>
      </div>

      <!-- Progress Steps -->
      <div class="wizard-steps">
        <div
          v-for="(step, idx) in steps"
          :key="idx"
          class="step"
          :class="{ active: currentStep === idx, completed: currentStep > idx }"
        >
          <span class="step-number">{{ currentStep > idx ? '✓' : idx + 1 }}</span>
          <span class="step-title">{{ step }}</span>
        </div>
      </div>

      <!-- Step Content -->
      <div class="wizard-content">
        <!-- Step 1: Data Input -->
        <div v-if="currentStep === 0" class="step-content">
          <div class="input-tabs">
            <button
              class="tab-btn"
              :class="{ active: inputMode === 'file' }"
              @click="inputMode = 'file'"
            >
              📁 Dosya Yükle
            </button>
            <button
              class="tab-btn"
              :class="{ active: inputMode === 'paste' }"
              @click="inputMode = 'paste'"
            >
              📋 Yapıştır
            </button>
          </div>

          <div class="context-input">
             <label>Bağlam / Not (Opsiyonel)</label>
             <textarea
               v-model="context"
               placeholder="Örn: Bu liste 2024 İzmir Kitap Fuarı katılımcılarıdır. Etiketleri buna göre öner."
               rows="2"
             ></textarea>
          </div>

          <div v-if="inputMode === 'file'" class="file-upload-area">
            <input
              type="file"
              ref="fileInput"
              @change="handleFileSelect"
              accept=".xlsx,.xls,.csv"
              style="display: none"
            />
            <div
              class="drop-zone"
              :class="{ 'is-dragover': isDragover, 'has-file': file }"
              @click="$refs.fileInput.click()"
              @dragover.prevent="isDragover = true"
              @dragleave.prevent="isDragover = false"
              @drop.prevent="handleDrop"
            >
              <div v-if="file" class="file-info">
                <span class="icon">📄</span>
                <span class="name">{{ file.name }}</span>
                <span class="size">({{ formatFileSize(file.size) }})</span>
                <button @click.stop="file = null" class="remove-file">×</button>
              </div>
              <div v-else class="upload-placeholder">
                <span class="icon">📥</span>
                <p>Excel veya CSV dosyasını sürükleyin</p>
                <span class="sub-text">veya tıklayarak seçin</span>
                <span class="formats">.xlsx, .xls, .csv</span>
              </div>
            </div>
          </div>
          <div v-if="inputMode === 'paste'" class="paste-area">
            <textarea
              v-model="pasteData"
              placeholder="Excel'den kopyaladığınız veriyi buraya yapıştırın...&#10;&#10;İlk satır kolon başlıkları olmalı.&#10;Örnek:&#10;isim	telefon	email	okul&#10;Ali Veli	5551234567	ali@test.com	ABC Lisesi"
              rows="12"
            ></textarea>
            <p class="hint">💡 Excel'den CTRL+C ile kopyalayıp CTRL+V ile yapıştırın</p>
          </div>


        </div>

        <!-- Step 2: AI Analysis & Mapping -->
        <div v-if="currentStep === 1" class="step-content">
          <div v-if="isAnalyzing" class="analyzing">
            <div class="spinner"></div>
            <p>🤖 AI veriyi analiz ediyor...</p>
          </div>

          <div v-else-if="analysis" class="analysis-result">
            <div class="summary">
              <div class="stat">
                <span class="value">{{ rowsCount }}</span>
                <span class="label">Satır</span>
              </div>
              <div class="stat">
                <span class="value">{{ headers.length }}</span>
                <span class="label">Kolon</span>
              </div>
              <div class="stat" v-if="analysis.suggested_labels?.length">
                <span class="value">{{ analysis.suggested_labels.length }}</span>
                <span class="label">Etiket Önerisi</span>
              </div>
            </div>

            <h4>🔗 Alan Eşleştirmeleri</h4>
            <div class="mappings-table">
              <div class="mapping-row header">
                <span>Kaynak Kolon</span>
                <span>→</span>
                <span>Sistem Alanı</span>
                <span>Güven</span>
              </div>
              <div
                v-for="mapping in analysis.mappings"
                :key="mapping.source_column"
                class="mapping-row"
              >
                <span class="source">{{ mapping.source_column }}</span>
                <span>→</span>
                <select v-model="mappings[mapping.source_column]">
                  <option value="">Yoksay</option>
                  <option value="name">İsim Soyisim</option>
                  <option value="first_name">Ad</option>
                  <option value="last_name">Soyad</option>
                  <option value="email">E-posta</option>
                  <option value="phone_number">Telefon</option>
                  <option value="school_name">Okul</option>
                  <option value="labels">Etiketler</option>
                  <option :value="'custom_' + mapping.source_column">Özel Alan</option>
                </select>
                <span class="confidence" :class="getConfidenceClass(mapping.confidence)">
                  {{ mapping.confidence }}%
                </span>
              </div>
            </div>

            <!-- School Matches Preview -->
            <div v-if="Object.keys(schoolMatches).length" class="school-matches">
              <h4>🏫 Okul Eşleştirmeleri</h4>
              <div class="school-list">
                <div
                  v-for="(match, name) in schoolMatches"
                  :key="name"
                  class="school-match"
                  :class="{ 'matched': match.matched, 'not-found': !match.matched }"
                >
                  <span class="original">{{ name }}</span>
                  <span class="arrow">→</span>
                  <span v-if="match.matched" class="matched">
                    ✓ {{ match.kurum }} ({{ match.ilce }})
                    <span class="confidence">{{ match.confidence }}%</span>
                  </span>
                  <span v-else class="not-found">
                    ❌ Eşleşmedi - Okul Kaydedilecek
                  </span>
                </div>
              </div>
            </div>

            <!-- Conditional Label Columns -->
            <div v-if="analysis.conditional_label_columns?.length" class="conditional-labels">
              <h4>🎯 Koşullu Etiketler</h4>
              <p class="hint">Bu kolonlardaki değere göre otomatik etiket atanacak</p>
              <div class="conditional-list">
                <div
                  v-for="(cond, idx) in analysis.conditional_label_columns"
                  :key="idx"
                  class="conditional-item"
                  :class="{ 'existing': cond.existing, 'new': !cond.existing }"
                >
                  <div class="cond-main">
                    <span class="column-name">{{ cond.column }}</span>
                    <span class="condition">= "{{ cond.condition }}"</span>
                    <span class="arrow">→</span>
                    <span class="label-name">
                      <span v-if="cond.existing" class="badge existing">✓</span>
                      <span v-else class="badge new">+</span>
                      {{ cond.label }}
                    </span>
                  </div>
                  <div class="cond-remove">
                    <label class="remove-label-selector">
                      <span class="remove-icon">🗑️</span>
                      <span>Kaldır:</span>
                      <select
                        v-model="conditionalRemoveLabels[idx]"
                        @change="updateConditionalRemoveLabel(idx)"
                        class="remove-select"
                      >
                        <option value="">Hiçbiri</option>
                        <option
                          v-for="label in existingLabels.filter(l => l !== cond.label)"
                          :key="label"
                          :value="label"
                        >
                          {{ label }}
                        </option>
                      </select>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Label Suggestions -->
            <div v-if="analysis.suggested_labels?.length" class="label-suggestions">
              <h4>🏷️ Önerilen Etiketler</h4>
              <div class="labels-list">
                <label
                  v-for="label in analysis.suggested_labels"
                  :key="label"
                  class="label-option"
                >
                  <input type="checkbox" v-model="selectedLabels" :value="label" />
                  {{ label }}
                </label>
              </div>
            </div>

            <!-- Existing Labels Selector -->
            <div class="existing-labels-section">
              <h4>📋 Mevcut Etiketlerden Seç</h4>
              <div class="label-search">
                <input
                  v-model="labelSearch"
                  placeholder="Etiket ara..."
                  class="label-search-input"
                />
              </div>
              <div class="existing-labels-list">
                <label
                  v-for="label in filteredExistingLabels"
                  :key="label"
                  class="label-option existing"
                  :class="{ selected: selectedLabels.includes(label) }"
                >
                  <input type="checkbox" v-model="selectedLabels" :value="label" />
                  {{ label }}
                </label>
                <p v-if="filteredExistingLabels.length === 0" class="no-results">
                  Eşleşen etiket bulunamadı
                </p>
              </div>
              <div class="new-label">
                <input v-model="newLabel" placeholder="Yeni etiket ekle..." />
                <button @click="addLabel" :disabled="!newLabel">+</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Preview -->
        <div v-if="currentStep === 2" class="step-content">
          <div v-if="isLoadingPreview" class="analyzing">
            <div class="spinner"></div>
            <p>Önizleme hazırlanıyor...</p>
          </div>

          <div v-else-if="preview" class="preview-result">
            <div class="preview-summary">
              <div class="stat success">
                <span class="value">{{ preview.new_contacts }}</span>
                <span class="label">Yeni Kişi</span>
              </div>
              <div class="stat warning">
                <span class="value">{{ preview.existing_contacts }}</span>
                <span class="label">Mevcut (Güncellenecek)</span>
              </div>
              <div class="stat">
                <span class="value">{{ preview.total }}</span>
                <span class="label">Toplam</span>
              </div>
            </div>

            <div class="update-option">
              <label>
                <input type="checkbox" v-model="updateExisting" />
                Mevcut kişileri güncelle
              </label>
            </div>

            <div class="update-option" v-if="analysis?.school_column">
              <label>
                <input type="checkbox" v-model="skipSchoolMatching" />
                Okul eşleştirmesini yoksay (okul bilgisini aynen kaydet)
              </label>
            </div>

            <h4>📋 Önizleme (İlk 20)</h4>
            <div class="preview-table">
              <table>
                <thead>
                  <tr>
                    <th>Durum</th>
                    <th>İsim</th>
                    <th>E-posta</th>
                    <th>Telefon</th>
                    <th>Okul</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in preview.preview"
                    :key="idx"
                    :class="item.action"
                  >
                    <td>
                      <span v-if="item.action === 'create'" class="badge create">Yeni</span>
                      <span v-else class="badge update">Güncelle</span>
                    </td>
                    <td>{{ item.row.name || `${item.row.first_name} ${item.row.last_name}` }}</td>
                    <td>{{ item.row.email }}</td>
                    <td>{{ item.row.phone_number }}</td>
                    <td>{{ item.row.school_name }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Step 4: Import -->
        <div v-if="currentStep === 3" class="step-content">
          <div v-if="isImporting" class="importing">
            <div class="spinner"></div>
            <p>📥 Kişiler aktarılıyor... (Arka planda devam ediyor)</p>
            <div class="progress-bar-container">
              <div class="progress-bar" :style="{ width: importProgress + '%' }"></div>
            </div>
            <p class="progress-text">{{ importProgress }}% tamamlandı</p>
            <p class="import-id-text" v-if="importId">Import ID: {{ importId }}</p>
            <div class="import-actions">
              <button @click="cancelImport" class="cancel-btn">🛑 İptal Et</button>
            </div>
            <p class="hint">Sayfa kapansa bile işlem devam edecek</p>
          </div>

          <div v-else-if="importResult" class="import-result">
            <div class="result-icon">🎉</div>
            <h3>İçe Aktarma Tamamlandı!</h3>

            <div class="result-stats">
              <div class="stat success">
                <span class="value">{{ importResult.results.created }}</span>
                <span class="label">Oluşturuldu</span>
              </div>
              <div class="stat warning">
                <span class="value">{{ importResult.results.updated }}</span>
                <span class="label">Güncellendi</span>
              </div>
              <div class="stat">
                <span class="value">{{ importResult.results.skipped }}</span>
                <span class="label">Atlandı</span>
              </div>
              <div v-if="importResult.results.errors?.length" class="stat error">
                <span class="value">{{ importResult.results.errors.length }}</span>
                <span class="label">Hata</span>
              </div>
            </div>

            <div class="backup-info">
              <p>💾 Yedek alındı:</p>
              <code>{{ importResult.backup_path }}</code>
            </div>

            <div v-if="importResult.results.errors?.length" class="errors-list">
              <h4>⚠️ Hatalar</h4>
              <ul>
                <li v-for="(err, idx) in importResult.results.errors.slice(0, 10)" :key="idx">
                  {{ err.error }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer / Navigation -->
      <div class="wizard-footer">
        <button
          v-if="currentStep > 0 && currentStep < 3"
          class="btn-secondary"
          @click="prevStep"
        >
          ← Geri
        </button>
        <div class="spacer"></div>
        <button
          v-if="currentStep === 0"
          class="btn-primary"
          :disabled="!canProceedStep1"
          @click="analyzeData"
        >
          Devam →
        </button>
        <button
          v-if="currentStep === 1"
          class="btn-primary"
          @click="loadPreview"
        >
          Önizleme →
        </button>
        <button
          v-if="currentStep === 2"
          class="btn-primary btn-success"
          @click="startImport"
        >
          🚀 İçe Aktar
        </button>
        <button
          v-if="currentStep === 3 && importResult"
          class="btn-primary"
          @click="close"
        >
          Kapat
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* global axios */

export default {
  name: 'ContactImportWizard',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    accountId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      currentStep: 0,
      steps: ['Veri Girişi', 'AI Analiz', 'Önizleme', 'İçe Aktar'],

      activeTab: 'data',
      inputMode: 'file', // file | paste
      file: null,
      pasteData: '',
      context: '',
      isDragging: false,
      isDragover: false,

      // Step 2
      isAnalyzing: false,
      analysis: null,
      headers: [],
      rowsCount: 0,
      sampleRows: [],
      importId: null,  // Server-side session ID
      mappings: {},
      schoolMatches: {},
      nameSplits: {},
      selectedLabels: [],
      existingLabels: [],
      labelSearch: '',
      conditionalRemoveLabels: [],  // For remove_labels feature
      newLabel: '',

      // Step 3
      isLoadingPreview: false,
      preview: null,
      updateExisting: false,
      skipSchoolMatching: false,

      // Step 4
      isImporting: false,
      importProgress: 0,
      importResult: null,
    };
  },

  computed: {
    canProceedStep1() {
      return this.file || this.pasteData.trim().length > 10;
    },
    filteredExistingLabels() {
      if (!this.labelSearch.trim()) {
        return this.existingLabels;
      }
      const search = this.labelSearch.toLowerCase();
      return this.existingLabels.filter(label =>
        label.toLowerCase().includes(search)
      );
    },
  },

  methods: {
    close() {
      this.$emit('close');
      this.resetWizard();
    },

    resetWizard() {
      this.currentStep = 0;
      this.file = null;
      this.pasteData = '';
      this.context = '';
      this.analysis = null;
      this.preview = null;
      this.importResult = null;
      this.mappings = {};
      this.selectedLabels = [];
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.file = file;
      }
    },

    handleFileDrop(event) {
      this.isDragover = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.file = file;
      }
    },

    handleDrop(event) {
      this.isDragover = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.file = file;
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },

    async analyzeData() {
      this.isAnalyzing = true;
      this.error = null;
      this.currentStep = 1;

      try {
        const formData = new FormData();
      if (this.inputMode === 'file') {
        formData.append('file', this.file);
      } else {
        formData.append('paste_data', this.pasteData);
      }
      formData.append('context', this.context);

        const response = await axios.post(
          `/api/v1/accounts/${this.accountId}/contact_import_wizard/analyze`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
          }
        );

        this.headers = response.data.headers;
        this.rowsCount = response.data.rows_count;
        this.sampleRows = response.data.sample_rows;
        this.importId = response.data.import_id;  // Save server session ID
        this.analysis = response.data.analysis;
        this.schoolMatches = response.data.school_matches || {};
        this.nameSplits = response.data.name_splits || {};
        this.existingLabels = response.data.existing_labels || [];  // Load existing labels

        // Set initial mappings from AI suggestions
        if (this.analysis.mappings) {
          this.analysis.mappings.forEach(m => {
            this.mappings[m.source_column] = m.target_field || '';
          });
        }

        // Set suggested labels
        if (this.analysis.suggested_labels) {
          this.selectedLabels = [...this.analysis.suggested_labels];
        }
      } catch (error) {
        console.error('Analysis failed:', error);
        alert('Analiz sırasında hata oluştu: ' + (error.response?.data?.error || error.message));
        this.currentStep = 0;
      } finally {
        this.isAnalyzing = false;
      }
    },

    async loadPreview() {
      this.isLoadingPreview = true;
      this.currentStep = 2;

      try {
        // Prepare school matches map
        const schoolMatchesMap = {};
        Object.entries(this.schoolMatches).forEach(([name, data]) => {
          if (data && (typeof data === 'number' || (data.match && data.match.id))) {
             schoolMatchesMap[name] = typeof data === 'number' ? data : data.match.id;
          }
        });

        const response = await axios.post(
          `/api/v1/accounts/${this.accountId}/contact_import_wizard/preview`,
          {
            import_id: this.importId,  // Use server-side data
            mappings: this.mappings,
            school_matches: schoolMatchesMap,
            name_splits: this.nameSplits
          },
          { withCredentials: true }
        );

        this.preview = response.data;
      } catch (error) {
        console.error('Preview failed:', error);
        alert('Önizleme oluşturulamadı');
        this.currentStep = 1;
      } finally {
        this.isLoadingPreview = false;
      }
    },

    async startImport() {
      if (!confirm(`${this.rowsCount} kişi içe aktarılacak. Bu işlem arka planda devam edecek. Başlatmak istiyor musunuz?`)) {
        return;
      }

      this.isImporting = true;
      this.currentStep = 3;
      this.importProgress = 0;

      try {
        // Build conditional_labels with remove_labels
        const enhancedConditionalLabels = (this.analysis?.conditional_label_columns || []).map((cond, idx) => ({
          ...cond,
          remove_labels: this.conditionalRemoveLabels[idx] ? [this.conditionalRemoveLabels[idx]] : []
        }));

        // Start background import
        const response = await axios.post(
          `/api/v1/accounts/${this.accountId}/contact_import_wizard/import`,
          {
            import_id: this.importId,
            mappings: this.mappings,
            school_column: this.analysis?.school_column,
            name_splits: this.nameSplits,
            labels: this.selectedLabels,
            conditional_labels: enhancedConditionalLabels,
            update_existing: this.updateExisting,
            skip_school_matching: this.skipSchoolMatching,
          },
          { withCredentials: true }
        );

        if (response.data.success) {
          // Start polling for progress
          this.pollImportStatus();
        } else {
          throw new Error(response.data.error || 'İçe aktarma başlatılamadı');
        }
      } catch (error) {
        console.error('Import failed:', error);
        alert('İçe aktarma sırasında hata: ' + (error.response?.data?.error || error.message));
        this.isImporting = false;
      }
    },

    async pollImportStatus() {
      const poll = async () => {
        try {
          const response = await axios.get(
            `/api/v1/accounts/${this.accountId}/contact_import_wizard/status?import_id=${this.importId}`,
            { withCredentials: true }
          );

          const status = response.data;
          this.importProgress = status.progress || 0;

          if (status.status === 'completed') {
            this.importResult = {
              success: true,
              results: {
                created: status.created || 0,
                updated: status.updated || 0,
                skipped: status.skipped || 0,
                errors: status.errors || []
              },
              backup_id: status.backup_id
            };
            this.isImporting = false;
          } else if (status.status === 'failed') {
            alert('İçe aktarma başarısız: ' + (status.error || 'Bilinmeyen hata'));
            this.isImporting = false;
          } else {
            // Still processing, poll again in 2 seconds
            setTimeout(poll, 2000);
          }
        } catch (error) {
          console.error('Status poll failed:', error);
          setTimeout(poll, 3000);
        }
      };

      poll();
    },

    async cancelImport() {
      if (!confirm('İmport işlemini iptal etmek istediğinize emin misiniz?')) return;

      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/contact_import_wizard/cancel`,
          { import_id: this.importId }
        );

        this.isImporting = false;
        alert('Import iptal edildi');
        this.currentStep = 0;
      } catch (error) {
        console.error('Cancel failed:', error);
        alert('İptal işlemi sırasında hata oluştu: ' + (error.response?.data?.error || error.message));
      }
    },


    getConfidenceClass(confidence) {
      if (confidence >= 80) return 'high';
      if (confidence >= 50) return 'medium';
      return 'low';
    },

    addLabel() {
      if (this.newLabel && !this.selectedLabels.includes(this.newLabel)) {
        this.selectedLabels.push(this.newLabel);
        this.newLabel = '';
      }
    },

    updateConditionalRemoveLabel(idx) {
      // This is called when user selects a label to remove
      // The value is already bound via v-model
      console.log(`Conditional ${idx}: remove_labels = ${this.conditionalRemoveLabels[idx]}`);
    },
  },
};
</script>

<style scoped>
.import-wizard-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.import-wizard-modal {
  background: #ffffff;
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.wizard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f8fafc;
}

.wizard-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #475569;
  padding: 4px 8px;
  border-radius: 4px;
}

.close-btn:hover {
  background: #f8fafc;
}

.wizard-steps {
  display: flex;
  padding: 16px 24px;
  gap: 8px;
  border-bottom: 1px solid #f8fafc;
  background: #fafafa;
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  color: #64748b;
  flex: 1;
  justify-content: center;
}

.step.active {
  background: #3b82f6;
  color: white;
}

.step.completed {
  background: #bbf7d0;
  color: #15803d;
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: currentColor;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

.step.active .step-number,
.step.completed .step-number {
  background: white;
  color: #3b82f6;
}

.step.completed .step-number {
  color: #15803d;
}

.wizard-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.step-content {
  min-height: 300px;
}

/* Input Tabs */
.input-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.tab-btn {
  padding: 10px 20px;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.tab-btn.active {
  border-color: #3b82f6;
  background: #dbeafe;
  color: #1d4ed8;
}

/* Dropzone */
.dropzone {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 48px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.dropzone:hover,
.dropzone.dragover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.dropzone-content .icon {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
}

.dropzone-content p {
  margin: 4px 0;
  color: #475569;
}

.dropzone-content .hint {
  font-size: 13px;
  color: #94a3b8;
}

.dropzone-content .formats {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 8px;
}

.selected-file {
  margin-top: 12px;
  padding: 12px 16px;
  background: #dcfce7;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.selected-file button {
  background: none;
  font-size: 13px;
  resize: vertical;
}

/* Context Input */
.context-input {
  margin-top: 20px;
}

.context-input label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
}

.context-input input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
}

.hint {
  font-size: 13px;
  color: #64748b;
  margin-top: 6px;
}

/* Analyzing State */
.analyzing, .importing {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Progress Bar */
.progress-bar-container {
  width: 80%;
  max-width: 400px;
  height: 12px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  margin: 12px 0;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #10b981);
  border-radius: 6px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 18px;
  font-weight: 600;
  color: #3b82f6;
}

.import-id-text {
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  margin-top: 8px;
}

.import-actions {
  margin-top: 16px;
}

.cancel-btn {
  padding: 10px 20px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;
}

.cancel-btn:hover {
  background: #dc2626;
}

/* Summary Stats */
.summary, .preview-summary, .result-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
}

.stat {
  text-align: center;
  padding: 16px 24px;
  background: #f8fafc;
  border-radius: 12px;
  flex: 1;
}

.stat .value {
  display: block;
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
}

.stat .label {
  font-size: 13px;
  color: #64748b;
}

.stat.success { background: #dcfce7; }
.stat.success .value { color: #16a34a; }

.stat.warning { background: #fef9c3; }
.stat.warning .value { color: #a16207; }

.stat.error { background: #fef2f2; }
.stat.error .value { color: #dc2626; }

/* Mappings Table */
.mappings-table {
  background: #fafafa;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
}

.mapping-row {
  display: grid;
  grid-template-columns: 1fr 30px 1fr 60px;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.mapping-row.header {
  background: #f1f5f9;
  font-weight: 600;
  font-size: 13px;
}

.mapping-row select {
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
}

.confidence {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
}

.confidence.high { background: #bbf7d0; color: #15803d; }
.confidence.medium { background: #fef08a; color: #a16207; }
.confidence.low { background: #fee2e2; color: #b91c1c; }

.school-match.not-found {
  background-color: #fee2e2;
  color: #b91c1c;
  border: 1px dashed #ef4444;
}

.context-input {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.context-input label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #374151;
}
.context-input textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: #f9fafb;
  resize: vertical;
}
.context-input textarea:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* School Matches */
.school-matches, .label-suggestions, .existing-labels-section {
  margin-top: 24px;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
}

/* Existing Labels Section */
.existing-labels-section {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}

.existing-labels-section h4 {
  color: #0369a1;
  margin: 0 0 12px 0;
}

.label-search-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 12px;
}

.label-search-input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.existing-labels-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 150px;
  overflow-y: auto;
  padding: 8px;
  background: white;
  border-radius: 6px;
  margin-bottom: 12px;
}

.label-option.existing {
  background: #e0f2fe;
  border: 1px solid #7dd3fc;
  padding: 6px 12px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.label-option.existing:hover {
  background: #bae6fd;
}

.label-option.existing.selected {
  background: #0ea5e9;
  color: white;
  border-color: #0284c7;
}

.no-results {
  color: #64748b;
  font-size: 13px;
  margin: 8px 0;
}

/* Conditional Label Item */
.conditional-item {
  background: #fefce8;
  border: 1px solid #fde047;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
}

.cond-main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cond-remove {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #fde047;
}

.remove-label-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #dc2626;
}

.remove-icon {
  font-size: 14px;
}

.remove-select {
  padding: 4px 8px;
  border: 1px solid #fca5a5;
  border-radius: 4px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 12px;
}

.remove-select:focus {
  outline: none;
  border-color: #dc2626;
}

.school-matches h4, .label-suggestions h4 {
  margin: 0 0 12px 0;
}

.school-match {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.school-match .original {
  flex: 1;
  font-weight: 500;
}

.school-match .matched {
  flex: 2;
  color: #15803d;
}

.school-match .not-found {
  color: #dc2626;
}

/* Labels */
.labels-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.label-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  cursor: pointer;
}

.label-option:has(input:checked) {
  background: #dbeafe;
  border-color: #3b82f6;
}

.new-label {
  display: flex;
  gap: 8px;
}

.new-label input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.new-label button {
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

/* Preview Table */
.preview-table {
  overflow-x: auto;
}

.preview-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.preview-table th, .preview-table td {
  padding: 10px 12px;
  border: 1px solid #f1f5f9;
  text-align: left;
}

.preview-table th {
  background: #f8fafc;
  font-weight: 600;
}

.preview-table tr.create {
  background: #f0fdf4;
}

.preview-table tr.update {
  background: #fefce8;
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.badge.create { background: #bbf7d0; color: #15803d; }
.badge.update { background: #fef08a; color: #a16207; }

/* Import Result */
.import-result {
  text-align: center;
  padding: 24px;
}

.result-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.import-result h3 {
  margin: 0 0 24px 0;
}

.backup-info {
  margin-top: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.backup-info code {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  word-break: break-all;
}

/* Footer */
.wizard-footer {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #f1f5f9;
  gap: 12px;
}

.spacer {
  flex: 1;
}

.btn-primary, .btn-secondary {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-primary:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

.btn-primary.btn-success {
  background: #22c55e;
}

.btn-primary.btn-success:hover {
  background: #16a34a;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

/* Update option */
.update-option {
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #fef9c3;
  border-radius: 8px;
}

.update-option label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
/* Modern Animated Drop Zone */
.file-upload-area {
  margin: 1.5rem 0;
}

.drop-zone {
  position: relative;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  overflow: hidden;
}

.drop-zone::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.drop-zone:hover {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.1), 0 8px 10px -6px rgba(59, 130, 246, 0.1);
}

.drop-zone:hover::before {
  opacity: 1;
}

.drop-zone.is-dragover {
  border-color: #3b82f6;
  border-style: solid;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1), 0 20px 25px -5px rgba(59, 130, 246, 0.2);
  transform: scale(1.02);
  animation: pulse 1s ease-in-out infinite;
}

.drop-zone.has-file {
  border-color: #10b981;
  border-style: solid;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.upload-placeholder {
  position: relative;
  z-index: 1;
}

.upload-placeholder .icon {
  font-size: 3.5rem;
  display: block;
  margin-bottom: 1rem;
  animation: float 3s ease-in-out infinite;
}

.upload-placeholder p {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0.5rem 0;
}

.upload-placeholder .sub-text {
  display: block;
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.5rem;
}

.upload-placeholder .formats {
  display: inline-block;
  margin-top: 0.75rem;
  padding: 0.25rem 0.75rem;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.file-info {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease-out;
}

.file-info .icon {
  font-size: 2rem;
  animation: bounce 0.5s ease-out;
}

.file-info .name {
  flex: 1;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-info .size {
  font-size: 0.875rem;
  color: #64748b;
}

.file-info .remove-file {
  width: 28px;
  height: 28px;
  border: none;
  background: #fee2e2;
  color: #ef4444;
  border-radius: 50%;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-info .remove-file:hover {
  background: #ef4444;
  color: white;
  transform: rotate(90deg);
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1), 0 20px 25px -5px rgba(59, 130, 246, 0.2); }
  50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.2), 0 20px 25px -5px rgba(59, 130, 246, 0.3); }
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes bounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

/* Conditional Labels Section */
.conditional-labels {
  margin-top: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  border: 1px solid #f59e0b;
}

.conditional-labels h4 {
  margin: 0 0 0.5rem 0;
  color: #92400e;
}

.conditional-labels .hint {
  font-size: 0.875rem;
  color: #b45309;
  margin-bottom: 1rem;
}

.conditional-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.conditional-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  border-radius: 8px;
  font-size: 0.875rem;
}

.conditional-item .column-name {
  font-weight: 600;
  color: #1e293b;
}

.conditional-item .condition {
  color: #64748b;
  font-family: monospace;
}

.conditional-item .arrow {
  color: #94a3b8;
}

.conditional-item .label-name {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 600;
  color: #059669;
}

.conditional-item .badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: bold;
}

.conditional-item .badge.existing {
  background: #10b981;
  color: white;
}

.conditional-item .badge.new {
  background: #3b82f6;
  color: white;
}

.conditional-item.new {
  border: 1px dashed #3b82f6;
  background: #eff6ff;
}

</style>
